import { isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { capitalize } from "lodash";

import { MULTIPLE_VALUES_RESOLUTION } from "@/constants/questions";
import { Question, QuestionType, Scaling } from "@/types/question";
import { formatDate } from "@/utils/formatters/date";
import { abbreviatedNumber } from "@/utils/formatters/number";
import {
  getPredictionDisplayValue,
  getQuestionDateFormatString,
} from "@/utils/formatters/prediction";
import {
  isMultipleChoiceOptionResolved,
  isUnsuccessfullyResolved,
} from "@/utils/questions/resolution";
import { formatValueUnit } from "@/utils/questions/units";

export function formatResolution({
  resolution,
  questionType,
  locale,
  actual_resolve_time,
  scaling,
  unit,
  completeBounds = false,
  longBounds = false,
  sigfigs,
  resolutions,
  multiResolution = false,
}: {
  resolution: number | string | null | undefined;
  questionType: QuestionType;
  locale: string;
  actual_resolve_time: string | null;
  scaling?: Scaling;
  unit?: string;
  completeBounds?: boolean;
  longBounds?: boolean;
  sigfigs?: number;
  // The resolved values of a multi-resolution question, used to display a
  // count when `resolution` is the multiple-values marker.
  resolutions?: string[] | null;
  multiResolution?: boolean;
}) {
  if (resolution === null || resolution === undefined) {
    return "-";
  }

  resolution = String(resolution);

  if (
    resolution === MULTIPLE_VALUES_RESOLUTION &&
    (multiResolution || questionType !== QuestionType.MultipleChoice)
  ) {
    return resolutions?.length
      ? `${resolutions.length} values`
      : "Multiple values";
  }

  if (["yes", "no"].includes(resolution)) {
    return capitalize(resolution);
  }

  if (isUnsuccessfullyResolved(resolution)) {
    return capitalize(resolution);
  }

  if (resolution === "below_lower_bound") {
    if (completeBounds && scaling) {
      return getPredictionDisplayValue(0, {
        questionType,
        scaling,
        actual_resolve_time,
        unit,
        precision: 10,
        longBounds,
      });
    }
    return "Below lower bound";
  }
  if (resolution === "above_upper_bound") {
    if (completeBounds && scaling) {
      return getPredictionDisplayValue(1, {
        questionType,
        scaling,
        actual_resolve_time,
        unit,
        precision: 10,
        longBounds,
      });
    }
    return "Above upper bound";
  }

  if (questionType === QuestionType.Date) {
    if (!isNaN(Number(resolution)) && resolution.trim() !== "") {
      const date = new Date(Number(resolution));
      if (isValid(date)) {
        const formattedDate = scaling
          ? formatInTimeZone(
              date,
              "UTC",
              getQuestionDateFormatString({
                scaling,
                actual_resolve_time,
                valueTimestamp: date.getTime() / 1000,
              })
            )
          : formatDate(locale, date);
        return `${formattedDate} UTC`;
      }
      return resolution;
    }

    const date = new Date(resolution);
    if (isValid(date)) {
      const formattedDate = scaling
        ? formatInTimeZone(
            date,
            "UTC",
            getQuestionDateFormatString({
              scaling,
              actual_resolve_time,
              valueTimestamp: date.getTime() / 1000,
            })
          )
        : formatDate(locale, date);
      return `${formattedDate} UTC`;
    }
    return resolution;
  }

  if (!isNaN(Number(resolution)) && resolution.trim() !== "") {
    return formatValueUnit(
      abbreviatedNumber(Number(resolution), sigfigs ?? 10, false),
      unit
    );
  }

  if (questionType === QuestionType.MultipleChoice) {
    return resolution;
  }

  return resolution;
}

export function formatMultipleChoiceResolution(
  question: Pick<Question, "multi_resolution" | "resolution" | "resolutions">,
  choice: string,
  showNoResolutions: boolean = true
) {
  const { resolution } = question;
  if (resolution === null || resolution === undefined) {
    return "-";
  }

  const stringResolution = String(resolution);

  if (isUnsuccessfullyResolved(stringResolution)) {
    return capitalize(stringResolution);
  }

  return isMultipleChoiceOptionResolved(question, choice)
    ? "Yes"
    : showNoResolutions
      ? "No"
      : null;
}
